import { Notification } from "@drone-js/core";
import type { PropertyData, UploadPhotoPictureTypeIdEnum } from "libs/codegen/property-onboarding";
import { Fragment, type FunctionComponent, useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useStaticDataAtomValue } from "../staticDataAtom";
import { pictureTypeMapper } from "./constants";
import { DropGrid } from "./upload/DropGrid";

interface Props {
    fieldPrefix?: string;
    roomPhotos?: boolean;
    roomId?: string;
    roomCaptionName?: string;
}

export const PhotosController: FunctionComponent<Props> = ({ fieldPrefix, roomPhotos, roomId, roomCaptionName }) => {
    const { control, formState, getFieldState } = useFormContext<PropertyData>();
    const {
        cmsData: {
            photo: { addAtLeast3PhotosToContinue },
        },
    } = useStaticDataAtomValue();

    const targetField = `${fieldPrefix ? fieldPrefix + "." : ""}photos`;
    const fieldArray = useFieldArray({
        control,
        name: targetField as "photos",
        rules: roomPhotos
            ? undefined
            : {
                  validate: {
                      atLeastThree: (v) => v.length >= 3 || addAtLeast3PhotosToContinue,
                      mainPhoto: (v) =>
                          v.some((p) => p.pictureTypeId === pictureTypeMapper.mainPhoto) || "Main photo is required",
                  },
              },
    });
    const { setValue } = useFormContext<PropertyData>();
    const { error } = getFieldState(targetField as "photos", formState);

    // Determine the picture type based on accommodation category and room photos flag
    const getPictureTypeId = (): UploadPhotoPictureTypeIdEnum => {
        if (roomPhotos) {
            // Room photos are only available for Hotel and MultipleNha
            return pictureTypeMapper.room as unknown as UploadPhotoPictureTypeIdEnum;
        }

        return pictureTypeMapper.property as unknown as UploadPhotoPictureTypeIdEnum;
    };

    const pictureTypeId = getPictureTypeId();

    useEffect(() => {
        // if every photo is not a main photo, then set the first one to main
        if (
            !roomPhotos &&
            fieldArray.fields.length > 0 &&
            fieldArray.fields.every((item) => item.pictureTypeId !== pictureTypeMapper.mainPhoto)
        )
            setValue(`${targetField}.0.pictureTypeId` as "photos.0.pictureTypeId", pictureTypeMapper.mainPhoto);

        if (roomPhotos && fieldArray.fields.length > 0) {
            // Set room photos picture type to room photo
            fieldArray.fields.forEach((_, index) => {
                setValue(`${targetField}.${index}.pictureTypeId` as "photos.0.pictureTypeId", pictureTypeMapper.room);
            });
        }
    }, [fieldArray.fields, roomPhotos]);

    return (
        <Fragment>
            {!roomPhotos && (error || fieldArray.fields.length < 3) && (
                <Notification
                    intent={error ? "error" : "notice"}
                    title={error ? error.root.message : addAtLeast3PhotosToContinue}
                    asset={{
                        id: error ? "fill.symbol.triangle-exclamation" : "fill.symbol.circle-info",
                    }}
                />
            )}
            <DropGrid
                disableMainPhoto={roomPhotos}
                targetField={targetField}
                pictureTypeId={pictureTypeId}
                roomId={roomId}
                roomCaptionName={roomCaptionName}
                {...fieldArray}
            />
        </Fragment>
    );
};
